import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useContent } from "@/lib/api";
import NAVIGATION from "@/data/navigation.json";

const ENTREE_RDV = NAVIGATION.rendez_vous;
import { MenuMobile, BoutonRdvMobile } from "@/components/MenuMobile";
import { reseauxPied } from "@/lib/organizationJsonLd.mjs";

const INDEX = NAVIGATION.menu;

/* Une page servie par le backend n'est pas une route du routeur : un <Link> ne
   ferait que pousser l'URL dans l'historique sans jamais la demander au serveur. */
function LienNav({ item, className, children, ...reste }) {
  return item.page_serveur
    ? <a href={item.to} className={className} {...reste}>{children}</a>
    : <Link to={item.to} className={className} {...reste}>{children}</Link>;
}

export function Logo({ light = false }) {
  return (
    <span className="t-logo" aria-label="SLIZ">
      SLI<span style={{ color: light ? "#fff" : "var(--roi)" }}>Z</span>
    </span>
  );
}

export function Nav() {
  const location = useLocation();
  const isHome = location.pathname === "/" || location.pathname === "";
  const [solid, setSolid] = useState(!isHome);

  useEffect(() => {
    if (!isHome) {
      setSolid(true);
      return;
    }
    const onScroll = () => setSolid(window.scrollY > window.innerHeight * 0.72);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  return (
    <header className={`nav ${solid ? "solid" : "on-cover"}`} data-testid="main-nav">
      <div className="wrap nav-inner">
        <Link to="/" className="nav-brand" data-testid="nav-brand" aria-label="SLIZ — accueil">
          <Logo />
          <span className="nav-brand-sub">Production &amp; régie publicitaire</span>
        </Link>
        <nav className="nav-index" aria-label="Sommaire">
          {INDEX.map((it) => (
            <LienNav key={it.n} item={it} className="nav-item" data-testid={`nav-item-${it.n}`}>
              <span className="l">{it.l}</span>
            </LienNav>
          ))}
        </nav>
        <BoutonRdvMobile />
        <MenuMobile />
        <Link to={ENTREE_RDV.to} className="btn btn-primary nav-cta" data-testid="nav-cta-rdv">
          {ENTREE_RDV.l}
        </Link>
      </div>
    </header>
  );
}

export function Footer() {
  const { data } = useContent();
  const marque = data?.settings?.marque || {};
  const reseaux = reseauxPied(marque);
  if (data && reseaux.length === 0) {
    console.error("Configuration Marque : liste « reseaux_sociaux » vide ou absente — aucun compte officiel n'est affiché.");
  }
  const services = data?.services || [];
  const studio = services.filter((s) => s.parent === "production-audiovisuelle-paris" || s.slug === "production-audiovisuelle-paris");
  const regie = services.filter((s) => s.parent === "regie-publicitaire-paris" || s.slug === "regie-publicitaire-paris");

  return (
    <footer className="footer" data-testid="footer">
      <div className="wrap" style={{ paddingTop: 88 }}>
        <div className="grid gap-12 md:grid-cols-4">
          <div>
            <div style={{ fontSize: 22, marginBottom: 18 }}>
              <Logo light />
            </div>
            <p className="t-body" style={{ color: "rgba(255,255,255,0.72)", fontSize: 14, maxWidth: 300 }}>
              {marque.description}
            </p>
            <p className="t-label" style={{ marginTop: 22 }}>
              anciennement {marque.ancienNom || "Grinch Médias"}
            </p>
            {reseaux.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 18, marginTop: 22 }}>
                {reseaux.map((r) => (
                  <a
                    key={r.url}
                    href={r.url}
                    target="_blank"
                    rel="noopener"
                    data-testid={`footer-reseau-${r.ordre}`}
                    style={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }}
                  >
                    {r.libelle}
                  </a>
                ))}
              </div>
            )}
          </div>
          <nav aria-label="Studio">
            <p className="t-label" style={{ marginBottom: 22 }}>Studio</p>
            <ul className="space-y-3" style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {studio.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}/`} style={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }} data-testid={`footer-studio-${s.slug}`}>
                    {s.h1}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <nav aria-label="Régie">
            <p className="t-label" style={{ marginBottom: 22 }}>Régie</p>
            <ul className="space-y-3" style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {regie.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}/`} style={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }} data-testid={`footer-regie-${s.slug}`}>
                    {s.h1}
                  </Link>
                </li>
              ))}
              <li>
                <LienNav item={NAVIGATION.pied_regie_estimer} style={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }} data-testid={NAVIGATION.pied_regie_estimer.testid}>
                  {NAVIGATION.pied_regie_estimer.l}
                </LienNav>
              </li>
            </ul>
          </nav>
          <nav aria-label="Maison">
            <p className="t-label" style={{ marginBottom: 22 }}>Maison</p>
            <ul className="space-y-3" style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {(data?.settings?.guide_regies?.publie === true
                ? [...NAVIGATION.maison, NAVIGATION.guide_regies]
                : NAVIGATION.maison).map((it) => (
                <li key={it.to}>
                  <Link to={it.to} style={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }} data-testid={it.testid}>{it.l}</Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        <div className="footer-sign" aria-hidden="true">SLIZ</div>
        <div className="footer-legal">
          <span className="t-label">{marque.legal || "© 2026 SLIZ — anciennement Grinch Médias."}</span>
          <span style={{ display: "flex", gap: 28 }}>
            {NAVIGATION.legal.map((it) => (
              <Link key={it.to} to={it.to} className="t-label" data-testid={it.testid}>{it.l}</Link>
            ))}
          </span>
        </div>
      </div>
    </footer>
  );
}
